/* LEGEND — left rail widget stack (watchlist + movers). Ported from widgets.jsx. */
import React from 'react';
import { fmtPx4, fmtVol, fmtPct } from '../lib/format';
import { SYMBOLS as MOCK_SYMBOLS } from '../data/mock';
import { subscribeHeatLeaderboard, subscribeHeatRotation } from '../data/source';
import { heatReceipt, heatBandColor } from './HeatStrip';

const { useState: useStateW, useRef: useRefW, useEffect: useEffectW } = React;

export const Icon = {
  search: () => React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 }, React.createElement('circle', { cx: 11, cy: 11, r: 7 }), React.createElement('path', { d: 'M21 21l-4.3-4.3' })),
  chev: () => React.createElement('svg', { className: 'chev', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2 }, React.createElement('path', { d: 'M6 9l6 6 6-6' })),
  grip: () => React.createElement('svg', { viewBox: '0 0 24 24', fill: 'currentColor' }, [[8, 6], [16, 6], [8, 12], [16, 12], [8, 18], [16, 18]].map((p, i) => React.createElement('circle', { key: i, cx: p[0], cy: p[1], r: 1.6 }))),
  x: () => React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2 }, React.createElement('path', { d: 'M6 6l12 12M18 6L6 18' })),
  plus: () => React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2 }, React.createElement('path', { d: 'M12 5v14M5 12h14' })),
  list: () => React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 }, React.createElement('path', { d: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01' })),
  fire: () => React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 }, React.createElement('path', { d: 'M12 3c0 3-4 4-4 8a4 4 0 0 0 8 0c0-1-1-2-1-2 0 2-3 2-3 0 0-2 4-3 0-6Z' })),
  bolt: () => React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 }, React.createElement('path', { d: 'M13 2L4 14h7l-1 8 9-12h-7z' })),
  // Falling tape — the short screener's mirror of `bolt`.
  drop: () => React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinejoin: 'round' }, React.createElement('path', { d: 'M3 7l6 6 4-4 8 8' }), React.createElement('path', { d: 'M21 12v5h-5' })),
  news: () => React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 }, React.createElement('rect', { x: 3, y: 4, width: 18, height: 16, rx: 2 }), React.createElement('path', { d: 'M7 8h10M7 12h10M7 16h6' })),
  pause: () => React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 }, React.createElement('rect', { x: 6, y: 5, width: 4, height: 14 }), React.createElement('rect', { x: 14, y: 5, width: 4, height: 14 })),
  layers: () => React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 }, React.createElement('path', { d: 'M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5' })),
};

function ChgPill({ chg }: any) {
  const up = chg >= 0;
  return React.createElement('span', { className: 'chg ' + (up ? 'up' : 'down') },
    React.createElement('svg', { width: 8, height: 8, viewBox: '0 0 8 8', style: { transform: up ? 'none' : 'rotate(180deg)' } }, React.createElement('path', { d: 'M4 1l3 5H1z', fill: 'currentColor' })),
    fmtPct(chg).replace('+', ''));
}

function SymbolRow({ s, cols, active, hasPos, posQty, onClick, onRemove, onAdd, kind, dragProps, alarm, heat, heatFlash }: any) {
  // Heat annotation (watchlist rows): a full-row-width progress bar along the
  // row's BOTTOM edge (fill = heat 0..1, red→amber→green by band) with the
  // numeric score printed next to the ticker so the bar has a scale. The bar
  // glows while the symbol is in the hot set; a rotation reuses the strip's
  // one-shot pulse. The row keeps the user's position — heat never reorders
  // the list.
  const hv = heat ? Math.max(0, Math.min(1, Number(heat.heat) || 0)) : 0;
  const hc = heatBandColor(hv);
  return React.createElement('div', Object.assign({
    className: 'srow' + (active ? ' active' : '') + (alarm ? ' lad-alarm lad-' + alarm : '') + (heatFlash ? ' heat-flash' : ''),
    style: { gridTemplateColumns: cols },
    title: heat ? heatReceipt(heat) : undefined,
    onClick,
  }, dragProps || {}),
    React.createElement('div', { className: 'tkr' },
      hasPos ? React.createElement('span', { className: 'has-pos', style: { background: s.posSide === 'short' ? 'var(--down)' : 'var(--up)' }, title: 'Open position' }) : null,
      s.ticker,
      hasPos && posQty ? React.createElement('span', { className: 'qty-badge' }, (s.posSide === 'short' ? '−' : '') + fmtVol(posQty)) : null,
      heat ? React.createElement('span', { className: 'row-heat-val mono', style: { color: hc } }, hv.toFixed(2)) : null,
      heat && heat.trend === 'up' ? React.createElement('span', { className: 'hs-trend up' }, '▲')
        : heat && heat.trend === 'down' ? React.createElement('span', { className: 'hs-trend down' }, '▼') : null),
    React.createElement('div', { className: 'last num' }, fmtPx4(s.last)),
    React.createElement('div', { className: 'chg-wrap' }, React.createElement(ChgPill, { chg: s.chg })),
    kind === 'movers' ? React.createElement('div', { className: 'vol' }, fmtVol(s.vol * 1e6) + ' · ' + s.rvol.toFixed(1) + 'x') : null,
    onRemove ? React.createElement('button', { className: 'row-x', onClick: (e: any) => { e.stopPropagation(); onRemove(); }, title: 'Remove' }, Icon.x()) : null,
    onAdd ? React.createElement('button', { className: 'row-add', onClick: (e: any) => { e.stopPropagation(); onAdd(); }, title: 'Add to watchlist' }, Icon.plus()) : null,
    heat ? React.createElement('span', { className: 'row-heat-underbar' + (heat.hot ? ' hot' : '') },
      React.createElement('i', { style: { width: Math.round(hv * 100) + '%', background: hc } })) : null);
}

export function WidgetCard({ w, collapsed, onToggle, onClose, count, title, children, reorder }: any) {
  const [over, setOver] = useStateW(false);
  return React.createElement('div', {
    className: 'widget' + (collapsed ? ' collapsed' : '') + (over ? ' drop-target' : ''),
    onDragOver: (e: any) => { if (reorder.draggingId && reorder.draggingId !== w.id) { e.preventDefault(); setOver(true); } },
    onDragLeave: () => setOver(false),
    onDrop: (e: any) => { e.preventDefault(); setOver(false); reorder.onDrop(w.id); },
  },
    React.createElement('div', { className: 'widget-head' },
      React.createElement('div', {
        className: 'grip', title: 'Drag to reorder', draggable: true,
        onDragStart: () => reorder.onStart(w.id), onDragEnd: reorder.onEnd,
      }, Icon.grip()),
      React.createElement('span', { className: 'wtitle' }, title),
      count != null ? React.createElement('span', { className: 'wcount mono' }, count) : null,
      React.createElement('div', { className: 'wactions' },
        React.createElement('button', { className: 'icon-btn sm', onClick: onToggle, title: collapsed ? 'Expand' : 'Collapse' }, Icon.chev()),
        React.createElement('button', { className: 'icon-btn sm', onClick: onClose, title: 'Remove widget' }, Icon.x()))),
    React.createElement('div', { className: 'widget-body' }, children));
}

export function Watchlist({ rows, activeSymbol, positions, alarms, onSelect, onRemove, onAdd, rowReorder, search, universe }: any) {
  const [q, setQ] = useStateW('');
  const [results, setResults] = useStateW<any[]>([]);
  const [searching, setSearching] = useStateW(false);
  const watched = new Set(rows.map((r: any) => r.ticker));

  // Heat annotations, subscribed HERE so the 1 Hz board re-renders only this
  // widget's subtree, never Terminal's tree (quote-flood lesson). The audible
  // cue stays in HeatStrip — rows only flash, one beep per rotation total.
  const [heat, setHeat] = useStateW<Record<string, any>>({});
  const [rowFlash, setRowFlash] = useStateW<Record<string, number>>({});
  const heatMsgRef = useRefW(0);
  const heatTimersRef = useRefW<Record<string, any>>({});
  useEffectW(() => {
    const timers = heatTimersRef.current;
    const offBoard = subscribeHeatLeaderboard((b: any) => {
      heatMsgRef.current = Date.now();
      const m: Record<string, any> = {};
      for (const s of b.symbols || []) m[s.symbol] = s;
      setHeat(m);
    });
    const offRot = subscribeHeatRotation((ev: any) => {
      setRowFlash((prev) => ({ ...prev, [ev.symbol]: ev.ts }));
      if (timers[ev.symbol]) clearTimeout(timers[ev.symbol]);
      timers[ev.symbol] = setTimeout(() => {
        setRowFlash((prev) => { const n = { ...prev }; delete n[ev.symbol]; return n; });
        delete timers[ev.symbol];
      }, 5200);
    });
    // Feed-dark watchdog: frozen heat bars read as live conviction — clear them
    // when the 1 Hz board goes quiet (transition-only, no steady-state renders).
    const watch = setInterval(() => {
      if (heatMsgRef.current && Date.now() - heatMsgRef.current >= 5000) {
        setHeat((prev) => (Object.keys(prev).length ? {} : prev));
      }
    }, 2000);
    return () => {
      offBoard(); offRot(); clearInterval(watch);
      Object.values(timers).forEach((t) => clearTimeout(t as any));
      heatTimersRef.current = {};
    };
  }, []);

  // Live symbol search (debounced) against the Realtime /api/symbols/search API,
  // with a local-universe fallback if no search fn is provided.
  useEffectW(() => {
    const query = q.trim();
    if (!query) { setResults([]); setSearching(false); return; }
    if (!search) {
      const list = universe || MOCK_SYMBOLS;
      setResults(list.filter((s: any) => s.ticker.toLowerCase().includes(query.toLowerCase()) || (s.name || '').toLowerCase().includes(query.toLowerCase())).slice(0, 8));
      return;
    }
    let alive = true;
    setSearching(true);
    const id = setTimeout(() => {
      Promise.resolve(search(query)).then((r: any[]) => { if (alive) { setResults((r || []).slice(0, 8)); setSearching(false); } })
        .catch(() => { if (alive) { setResults([]); setSearching(false); } });
    }, 250);
    return () => { alive = false; clearTimeout(id); };
  }, [q, search]);

  const add = (s: any) => { onAdd && onAdd(s); };
  const cols = '1fr auto 58px';
  return React.createElement('div', null,
    React.createElement('div', { className: 'wsearch' }, Icon.search(),
      React.createElement('input', { placeholder: 'Add or search symbol…', value: q, onChange: (e: any) => setQ(e.target.value) }),
      q ? React.createElement('button', { className: 'icon-btn sm', onClick: () => setQ('') }, Icon.x()) : React.createElement('kbd', null, '/')),
    q ? React.createElement('div', { style: { padding: '0 0 6px' } },
      results.length ? results.map((s: any) => React.createElement('div', {
        key: s.ticker, className: 'srow' + (watched.has(s.ticker) ? ' active' : ''), style: { gridTemplateColumns: cols, cursor: 'pointer' },
        onClick: () => { add(s); setQ(''); },
        title: 'Add to watchlist',
      },
        React.createElement('div', { className: 'tkr' }, s.ticker, React.createElement('span', { className: 'muted', style: { fontWeight: 400, fontSize: 10, marginLeft: 4 } }, s.name)),
        React.createElement('div', { className: 'last num' }, fmtPx4(s.last ?? s.base ?? 0)),
        watched.has(s.ticker)
          ? React.createElement('span', { className: 'muted', style: { marginLeft: 'auto', fontSize: 10 }, title: 'On watchlist' }, '✓')
          : React.createElement('button', {
              className: 'row-add', style: { display: 'flex', position: 'static', transform: 'none', marginLeft: 'auto' },
              title: 'Add to watchlist', onClick: (e: any) => { e.stopPropagation(); add(s); },
            }, Icon.plus())))
        : React.createElement('div', { style: { padding: '10px 12px', color: 'var(--t4)', fontSize: 12 } },
            searching ? 'Searching…' : 'No match for “' + q + '”'))
      : rows.map((s: any) => React.createElement(SymbolRow, {
        key: s.ticker, s, cols, active: s.ticker === activeSymbol,
        alarm: alarms && alarms[s.ticker],
        heat: heat[s.ticker], heatFlash: !!rowFlash[s.ticker],
        hasPos: !!positions[s.ticker], posQty: positions[s.ticker] && positions[s.ticker].qty,
        onClick: () => onSelect(s.ticker), onRemove: () => onRemove(s.ticker), kind: 'watch',
        dragProps: {
          draggable: true,
          onDragStart: () => rowReorder.onStart(s.ticker),
          onDragOver: (e: any) => { if (rowReorder.dragging && rowReorder.dragging !== s.ticker) { e.preventDefault(); } },
          onDrop: (e: any) => { e.preventDefault(); rowReorder.onDrop(s.ticker); },
          onDragEnd: rowReorder.onEnd,
        },
      })));
}

export function DailyMovers({ rows, activeSymbol, positions, onSelect, onAddToWatchlist }: any) {
  const cols = '1fr auto 52px 84px';
  return React.createElement('div', null,
    rows.map((s: any) => React.createElement(SymbolRow, {
      key: s.ticker, s, cols, active: s.ticker === activeSymbol,
      hasPos: !!positions[s.ticker], posQty: positions[s.ticker] && positions[s.ticker].qty,
      onClick: () => onSelect(s.ticker), onAdd: () => onAddToWatchlist(s.ticker), kind: 'movers',
    })));
}

export const WIDGET_TYPES = [
  { type: 'watchlist', title: 'Watchlist', icon: Icon.list, sub: 'added' },
  { type: 'scanner', title: 'Screener', icon: Icon.bolt, sub: 'added' },
  { type: 'scanner-short', title: 'Screener Short', icon: Icon.drop, sub: 'added' },
  { type: 'movers', title: 'Daily Movers', icon: Icon.fire, sub: 'added' },
  { type: 'gainers', title: 'Top Gainers', icon: Icon.bolt, sub: 'soon', disabled: true },
  { type: 'news', title: 'News', icon: Icon.news, sub: 'added' },
  { type: 'halts', title: 'Halts', icon: Icon.pause, sub: 'soon', disabled: true },
  { type: 'level2', title: 'Level 2', icon: Icon.layers, sub: 'soon', disabled: true },
];

export function AddWidgetMenu({ onAdd, onClose, present }: any) {
  const ref = useRefW<any>(null);
  React.useEffect(() => {
    const h = (e: any) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    setTimeout(() => document.addEventListener('mousedown', h), 0);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return React.createElement('div', { className: 'menu', ref, style: { right: 10, top: 38 } },
    React.createElement('div', { className: 'mhead' }, 'Add widget'),
    WIDGET_TYPES.map((t) => React.createElement('div', {
      key: t.type, className: 'mitem' + (t.disabled ? ' disabled' : ''),
      onClick: () => { if (!t.disabled) { onAdd(t.type); onClose(); } },
    },
      React.createElement('span', { className: 'mi-ic' }, t.icon()),
      React.createElement('span', null, t.title),
      React.createElement('span', { className: 'mi-sub' }, t.disabled ? 'soon' : (present.includes(t.type) ? 'add another' : 'add')))));
}
